import { ScoringRunStatus, type StudyRunCaseSelectDto } from '@@types/api.types';
import { BlueLabel } from '@components/shared/blue-label.component';
import { Badge, type BadgeProps } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { PlayIcon } from 'lucide-react';
import { type FC } from 'react';
import { formatEpochLabel, formatRunCaseDate, formatRunCaseLabel, hasValidRunCaseId } from './meia-p4-run-cases.util';

type RunCaseHeaderProps = {
  canEdit: boolean;
  isLatest: boolean;
  isRunDisabled?: boolean;
  onOpenScoringConfig: () => void;
  onRefresh: () => void;
  onSelect: (runCaseId: string) => void;
  runCases: StudyRunCaseSelectDto[];
  runningRunCaseId?: string;
  runDisabledReason?: string;
  selectedRunCase: StudyRunCaseSelectDto;
};

export const RunCaseHeader: FC<RunCaseHeaderProps> = (props) => {
  const {
    canEdit,
    isLatest,
    isRunDisabled = false,
    onOpenScoringConfig,
    onRefresh,
    onSelect,
    runCases,
    runDisabledReason,
    runningRunCaseId,
    selectedRunCase,
  } = props;
  const hasValidId = hasValidRunCaseId(selectedRunCase);
  const isRunning = runningRunCaseId === selectedRunCase.id;

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
      <div className="flex min-w-0 flex-col gap-1.5">
        <BlueLabel
          className="text-xs"
          text={isLatest ? 'Latest Run' : 'Run Case'}
        />
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-2xl font-semibold text-gray-950">{runCaseVersionLabel(selectedRunCase)}</span>
          <Badge variant={runStatusVariant(selectedRunCase.status)}>{runStatusLabel(selectedRunCase.status)}</Badge>
          <Badge variant="outline">{formatEpochLabel(selectedRunCase.epoch)}</Badge>
          {isRunning ? <Badge variant="approved">Running</Badge> : null}
        </div>
        <span className="text-xs text-gray-500">Created {formatRunCaseDate(selectedRunCase.createdAt)}</span>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {hasValidId ? (
          <Select
            onValueChange={onSelect}
            value={selectedRunCase.id}
          >
            <SelectTrigger className="w-full min-w-[180px] bg-white md:w-[200px]">
              <SelectValue placeholder="Select run">{runCaseVersionLabel(selectedRunCase)}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {runCases.filter(hasValidRunCaseId).map((runCase) => (
                <SelectItem
                  key={runCase.id}
                  value={runCase.id}
                >
                  {runCaseVersionLabel(runCase)} · {formatRunCaseDate(runCase.createdAt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Button
            onClick={onRefresh}
            size="sm"
            type="button"
            variant="secondary"
          >
            Refresh Data
          </Button>
        )}

        {canEdit ? (
          <Button
            disabled={isRunDisabled}
            onClick={onOpenScoringConfig}
            size="sm"
            title={runDisabledReason}
            type="button"
          >
            <PlayIcon className="size-4" />
            Run Epoch
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const runCaseVersionLabel = (runCase: StudyRunCaseSelectDto): string => formatRunCaseLabel({ runCase });

export const runStatusLabel = (status: ScoringRunStatus): string => {
  switch (status) {
    case ScoringRunStatus.COMPLETE:
      return 'Complete';
    case ScoringRunStatus.FAILED:
      return 'Failed';
    case ScoringRunStatus.PENDING:
      return 'Pending';
    case ScoringRunStatus.RUNNING:
      return 'Running';
    case ScoringRunStatus.STALE:
      return 'Stale';
  }
};

const runStatusVariant = (status: ScoringRunStatus): BadgeProps['variant'] => {
  switch (status) {
    case ScoringRunStatus.COMPLETE:
      return 'success';
    case ScoringRunStatus.FAILED:
      return 'destructive';
    case ScoringRunStatus.PENDING:
      return 'pending';
    case ScoringRunStatus.RUNNING:
      return 'approved';
    case ScoringRunStatus.STALE:
      return 'warning';
  }
};
